import type {
  Adsr,
  FilterConfig,
  WavetablePreset,
  WtModRoute,
  WtOscConfig,
  WtWarpMode,
} from "../types";
import { deepClone } from "../kernel/deepClone";
import { defaultWavetableTableId } from "./catalog";
import { WAVETABLE_MAX_UNISON } from "./types";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const DEFAULT_FILTER: FilterConfig = {
  type: "lowpass",
  cutoffHz: 4200,
  resonance: 0.25,
};

const DEFAULT_AMP: Adsr = { a: 0.01, d: 0.2, s: 0.7, r: 0.35 };
const DEFAULT_MOD: Adsr = { a: 0.02, d: 0.35, s: 0.4, r: 0.4 };

const WARP_MODES: readonly WtWarpMode[] = ["none", "bend", "mirror", "sync"];

function normalizeWarpMode(raw: unknown): WtWarpMode {
  return typeof raw === "string" && (WARP_MODES as readonly string[]).includes(raw)
    ? (raw as WtWarpMode)
    : "none";
}

function normalizeOsc(raw: Partial<WtOscConfig> | undefined, fallbackTable: string): WtOscConfig {
  return {
    tableId: typeof raw?.tableId === "string" && raw.tableId ? raw.tableId : fallbackTable,
    position: clamp(Number(raw?.position ?? 0.25), 0, 1),
    warp: clamp(Number(raw?.warp ?? 0), 0, 1),
    warpMode: normalizeWarpMode(raw?.warpMode),
    octave: clamp(Math.round(Number(raw?.octave ?? 0)), -3, 3),
    semis: clamp(Math.round(Number(raw?.semis ?? 0)), -12, 12),
    detuneCents: clamp(Number(raw?.detuneCents ?? 0), -100, 100),
    level: clamp(Number(raw?.level ?? 0.7), 0, 1),
  };
}

function normalizeRoutes(raw: unknown): WtModRoute[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: WtModRoute[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const route = item as Partial<WtModRoute>;
    if (typeof route.source !== "string" || typeof route.target !== "string") {
      continue;
    }
    out.push({
      source: route.source as WtModRoute["source"],
      target: route.target as WtModRoute["target"],
      depth: Number(route.depth) || 0,
      ...(route.invert ? { invert: true } : {}),
    });
  }
  return out;
}

export function createDefaultWavetablePreset(name = "Wavetable"): WavetablePreset {
  const table = defaultWavetableTableId();
  return {
    name,
    voices: 5,
    unisonDetune: 12,
    unisonSpread: 0.65,
    unisonEdgeDecay: 0.25,
    osc1: normalizeOsc({ tableId: table, position: 0.2, level: 0.75 }, table),
    osc2: normalizeOsc({ tableId: table, position: 0.55, level: 0.55, detuneCents: 7 }, table),
    sync: false,
    pmDepth: 0,
    sub: { wave: "sine", octave: -1, level: 0.2 },
    noise: { level: 0 },
    filter: { ...DEFAULT_FILTER },
    ampEnv: { ...DEFAULT_AMP },
    modEnv: { ...DEFAULT_MOD },
    lfo: { rateHz: 0.35, wave: "sine", depth: 0.15 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.45 },
      { source: "lfo", target: "tablePos1", depth: 0.12 },
    ],
    glideMs: 0,
  };
}

export function normalizeWavetablePreset(raw: unknown): WavetablePreset {
  const base = createDefaultWavetablePreset();
  if (!raw || typeof raw !== "object") {
    return base;
  }
  const p = raw as Partial<WavetablePreset>;
  const table = defaultWavetableTableId();
  return {
    name: typeof p.name === "string" && p.name.trim() ? p.name.trim() : base.name,
    voices: clamp(Math.round(Number(p.voices ?? base.voices)), 1, WAVETABLE_MAX_UNISON),
    unisonDetune: clamp(Number(p.unisonDetune ?? base.unisonDetune), 0, 100),
    unisonSpread: clamp(Number(p.unisonSpread ?? base.unisonSpread), 0, 1),
    unisonEdgeDecay: clamp(Number(p.unisonEdgeDecay ?? base.unisonEdgeDecay), 0, 1),
    osc1: normalizeOsc(p.osc1, table),
    osc2: normalizeOsc(p.osc2, table),
    sync: !!p.sync,
    pmDepth: clamp(Number(p.pmDepth ?? 0), 0, 4),
    sub: {
      wave: p.sub?.wave === "square" ? "square" : "sine",
      octave: clamp(Math.round(Number(p.sub?.octave ?? -1)), -2, 0),
      level: clamp(Number(p.sub?.level ?? 0.2), 0, 1),
    },
    noise: { level: clamp(Number(p.noise?.level ?? 0), 0, 1) },
    filter: {
      type: p.filter?.type === "highpass" || p.filter?.type === "bandpass"
        ? p.filter.type
        : "lowpass",
      cutoffHz: clamp(Number(p.filter?.cutoffHz ?? DEFAULT_FILTER.cutoffHz), 40, 18000),
      resonance: clamp(Number(p.filter?.resonance ?? DEFAULT_FILTER.resonance), 0, 1),
    },
    ampEnv: {
      a: clamp(Number(p.ampEnv?.a ?? DEFAULT_AMP.a), 0.001, 5),
      d: clamp(Number(p.ampEnv?.d ?? DEFAULT_AMP.d), 0.001, 5),
      s: clamp(Number(p.ampEnv?.s ?? DEFAULT_AMP.s), 0, 1),
      r: clamp(Number(p.ampEnv?.r ?? DEFAULT_AMP.r), 0.001, 8),
    },
    modEnv: {
      a: clamp(Number(p.modEnv?.a ?? DEFAULT_MOD.a), 0.001, 5),
      d: clamp(Number(p.modEnv?.d ?? DEFAULT_MOD.d), 0.001, 5),
      s: clamp(Number(p.modEnv?.s ?? DEFAULT_MOD.s), 0, 1),
      r: clamp(Number(p.modEnv?.r ?? DEFAULT_MOD.r), 0.001, 8),
    },
    lfo: {
      rateHz: clamp(Number(p.lfo?.rateHz ?? 0.35), 0.01, 40),
      wave: p.lfo?.wave === "tri" ? "tri" : "sine",
      depth: clamp(Number(p.lfo?.depth ?? 0.15), 0, 1),
    },
    routes: normalizeRoutes(p.routes),
    glideMs: clamp(Number(p.glideMs ?? 0), 0, 2000),
  };
}

export function cloneWavetablePreset(preset: WavetablePreset): WavetablePreset {
  return deepClone(normalizeWavetablePreset(preset));
}

export function wavetableInstrument(preset: WavetablePreset): {
  kind: "wavetable";
  preset: WavetablePreset;
} {
  return { kind: "wavetable", preset: cloneWavetablePreset(preset) };
}

export function isWavetableInstrument(
  instrument: { kind: string },
): instrument is { kind: "wavetable"; preset: WavetablePreset } {
  return instrument.kind === "wavetable";
}

/** Collect table ids referenced by a preset (for lazy host loads). */
export function wavetablePresetTableIds(preset: WavetablePreset): string[] {
  const ids = new Set<string>();
  if (preset.osc1.tableId) {
    ids.add(preset.osc1.tableId);
  }
  if (preset.osc2.tableId) {
    ids.add(preset.osc2.tableId);
  }
  return [...ids];
}
